import { createAuthenticatedApi } from './apiInterceptors';

// Use shared authenticated API instance with token refresh
const api = createAuthenticatedApi();

export interface Subtask {
  id: string;
  todoId: string;
  title: string;
  completed: boolean;
  completedAt?: string | null;
  orderIndex: number;
  createdAt: string;
}

export interface SubtaskStats {
  done: number;
  total: number;
}

export interface Tag {
  id: string;
  name: string;
  color: string;
}

export interface TodoComment {
  id: string;
  todoId: string;
  body: string;
  /** 'mcp': written by an AI client (clientName, e.g. "Claude") instead of the user in the app */
  source?: 'web' | 'mcp';
  clientName?: string | null;
  editedAt?: string | null;
  createdAt: string;
}

export type TodoActivityType =
  | 'CREATED' | 'TITLE_CHANGED' | 'DESCRIPTION_CHANGED' | 'PRIORITY_CHANGED' | 'DUE_CHANGED'
  | 'COMPLETED' | 'REOPENED' | 'MOVED' | 'SUBTASK_ADDED' | 'SUBTASK_DONE' | 'COMMENTED' | 'TAGS_CHANGED';

export interface TodoActivity {
  id: string;
  todoId: string;
  type: TodoActivityType;
  meta: Record<string, string | number | null> | null;
  createdAt: string;
}

export interface Todo {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  priority: 'low' | 'medium' | 'high';
  orderIndex: number;  // Sıralama için yeni alan
  createdAt: string;
  subGoalId: string;
  isStarted?: boolean;
  startedAt?: string | null;
  stoppedAt?: string | null;
  completedAt?: string | null;
  completionNote?: string | null;
  isTimely?: boolean;
  totalTimeSpent?: number;
  dueDate?: string | null;
  reminderAt?: string | null;
  tags?: Tag[];
  estimatedSeconds?: number | null;
  pomodoroCount?: number;
  subGoal?: {
    id: string;
    title: string;
    goalId?: string;
    goal?: {
      id: string;
      title: string;
    };
  };
  goal?: {
    id: string;
    title: string;
  };
}

export interface SubGoal {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  createdAt: string;
  todos: Todo[];
  goal?: {
    id: string;
    title: string;
  };
}

export interface Goal {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  completed: boolean;
  isFavorite?: boolean;
  createdAt: string;
  /** Last content change in the goal or under it (T-115); missing on older servers. */
  updatedAt?: string;
  subGoals: SubGoal[];
}

export interface TodosResponse {
  todos: Todo[];
  total: number;
  completed: number;
  pending: number;
}

export interface TodoStatistics {
  total: number;
  completed: number;
  pending: number;
  completionPercentage: number;
  priorityBreakdown: {
    high: number;
    medium: number;
    low: number;
  };
}

export interface TodosByPriorityResponse {
  todos: Todo[];
  priority: string;
  total: number;
}

export interface TodosByStatusResponse {
  todos: Todo[];
  status: string;
  total: number;
}

export const goalService = {
  // Goals
  getGoals: async (): Promise<Goal[]> => {
    const response = await api.get('/goals');
    return response.data;
  },

  getGoal: async (id: string): Promise<Goal> => {
    const response = await api.get(`/goals/${id}`);
    return response.data;
  },

  createGoal: async (goalData: Partial<Goal>): Promise<Goal> => {
    const response = await api.post('/goals', goalData);
    return response.data;
  },

  updateGoal: async (id: string, goalData: Partial<Goal>): Promise<Goal> => {
    const response = await api.put(`/goals/${id}`, goalData);
    return response.data;
  },

  deleteGoal: async (id: string): Promise<void> => {
    await api.delete(`/goals/${id}`);
  },

  toggleFavorite: async (id: string): Promise<{ isFavorite: boolean }> => {
    const response = await api.put(`/goals/${id}/favorite`);
    return response.data;
  },

  // SubGoals
  createSubGoal: async (goalId: string, subGoalData: Partial<SubGoal>): Promise<SubGoal> => {
    const response = await api.post(`/goals/${goalId}/subgoals`, subGoalData);
    return response.data;
  },

  updateSubGoal: async (goalId: string, subGoalId: string, subGoalData: Partial<SubGoal>): Promise<SubGoal> => {
    const response = await api.put(`/goals/${goalId}/subgoals/${subGoalId}`, subGoalData);
    return response.data;
  },

  deleteSubGoal: async (goalId: string, subGoalId: string): Promise<void> => {
    await api.delete(`/goals/${goalId}/subgoals/${subGoalId}`);
  },

  // Todos
  createTodo: async (todoData: {
    subGoalId: string;
    title: string;
    description?: string;
    priority?: 'low' | 'medium' | 'high';
    orderIndex?: number;
  }): Promise<Todo> => {
    const payload = {
      title: todoData.title,
      description: todoData.description || '',
      priority: todoData.priority || 'medium',
      ...(typeof todoData.orderIndex === 'number' ? { orderIndex: todoData.orderIndex } : {})
    };
    const response = await api.post(`/subgoals/${todoData.subGoalId}/todos`, payload);
    return response.data;
  },

  updateTodo: async (subGoalId: string, todoId: string, todoData: Partial<Todo>): Promise<Todo> => {
    const response = await api.put(`/subgoals/${subGoalId}/todos/${todoId}`, todoData);
    return response.data;
  },

  deleteTodo: async (todoId: string): Promise<void> => {
    // We need to get the todo first to find its subgoal
    const todosResponse = await api.get('/todos');
    const todos = todosResponse.data.todos || todosResponse.data;
    const todo = todos.find((t: Todo) => t.id === todoId);
    
    if (!todo) {
      throw new Error('Todo not found');
    }
    
    await api.delete(`/subgoals/${todo.subGoalId}/todos/${todoId}`);
  },

  moveTodo: async (todoId: string, targetSubgoalId: string): Promise<Todo> => {
    const response = await api.post(`/todos/${todoId}/move`, { targetSubgoalId });
    return response.data.todo;
  },

  // Enhanced Todo API methods
  getAllTodos: async (): Promise<TodosResponse> => {
    const response = await api.get('/todos');
    return response.data; // Returns enhanced response with metadata
  },

  getTodosByPriority: async (priority: 'low' | 'medium' | 'high'): Promise<TodosByPriorityResponse> => {
    const response = await api.get(`/todos/priority/${priority}`);
    return response.data;
  },

  getCompletedTodos: async (): Promise<TodosByStatusResponse> => {
    const response = await api.get('/todos/completed');
    return response.data;
  },

  getPendingTodos: async (): Promise<TodosByStatusResponse> => {
    const response = await api.get('/todos/pending');
    return response.data;
  },

  getTodoStatistics: async (): Promise<TodoStatistics> => {
    const response = await api.get('/todos/stats');
    return response.data;
  },

  reorderTodos: async (subGoalId: string, todoOrders: Array<{ id: string; orderIndex: number }>): Promise<{ message: string; updatedTodos: Todo[] }> => {
    const response = await api.post(`/subgoals/${subGoalId}/todos/reorder`, {
      todoOrders
    });
    return response.data;
  },

  toggleTodoCompletion: async (todoId: string, completionNote?: string): Promise<Todo> => {
    const requestBody = completionNote ? { completionNote } : {};
    
    // Use the direct toggle endpoint that works for both regular and quick tasks
    const response = await api.post(`/todos/${todoId}/toggle`, requestBody);
    
    return response.data.todo;
  },

  // SubGoal direct access methods
  getSubGoalDirect: async (subgoalId: string): Promise<SubGoal> => {
    const response = await api.get(`/subgoals/${subgoalId}`);
    return response.data;
  },

  getSubGoalTodos: async (subgoalId: string): Promise<Todo[]> => {
    const response = await api.get(`/subgoals/${subgoalId}/todos`);
    // API artık { todos: [...] } formatında dönüyor
    if (Array.isArray(response.data)) {
      // Eski format desteği
      return response.data;
    }
    return response.data.todos || [];
  },

  getTodo: async (todoId: string): Promise<{
    todo: Todo;
    subGoal: { id: string; title: string; goalId: string } | null;
    subtasks: Subtask[];
    subtaskStats: SubtaskStats;
    tags: Tag[];
  }> => {
    const response = await api.get(`/todos/${todoId}`);
    return response.data;
  },

  getDueReminders: async (): Promise<Todo[]> => {
    const response = await api.get('/todos/reminders/due');
    return response.data.todos || [];
  },

  bulkCreateSubtasks: async (todoId: string, titles: string[]): Promise<{ subtasks: Subtask[]; stats: SubtaskStats }> => {
    const response = await api.post(`/todos/${todoId}/subtasks/bulk`, { titles });
    return response.data;
  },

  aiSuggestSubtasks: async (todoId: string, hint?: string): Promise<string[]> => {
    const response = await api.post(`/todos/${todoId}/ai/subtasks`, { hint: hint || '' });
    return response.data.suggestions;
  },

  aiImproveDescription: async (todoId: string, mode: 'summarize' | 'improve', hint?: string): Promise<string> => {
    const response = await api.post(`/todos/${todoId}/ai/improve-description`, { mode, hint: hint || '' });
    return response.data.description;
  },

  aiCompletionNote: async (todoId: string): Promise<string> => {
    const response = await api.post(`/todos/${todoId}/ai/completion-note`, {});
    return response.data.note;
  },

  completePomodoro: async (todoId: string): Promise<number> => {
    const response = await api.post(`/todos/${todoId}/pomodoro`);
    return response.data.pomodoroCount;
  },

  getTags: async (): Promise<Tag[]> => {
    const response = await api.get('/tags');
    return response.data.tags;
  },

  createTag: async (name: string, color: string): Promise<Tag> => {
    const response = await api.post('/tags', { name, color });
    return response.data;
  },

  deleteTag: async (tagId: string): Promise<void> => {
    await api.delete(`/tags/${tagId}`);
  },

  setTodoTags: async (todoId: string, tagIds: string[]): Promise<Tag[]> => {
    const response = await api.put(`/todos/${todoId}/tags`, { tagIds });
    return response.data.tags;
  },

  getComments: async (todoId: string): Promise<TodoComment[]> => {
    const response = await api.get(`/todos/${todoId}/comments`);
    return response.data.comments;
  },

  createComment: async (todoId: string, body: string): Promise<TodoComment> => {
    const response = await api.post(`/todos/${todoId}/comments`, { body });
    return response.data;
  },

  updateComment: async (todoId: string, commentId: string, body: string): Promise<TodoComment> => {
    const response = await api.patch(`/todos/${todoId}/comments/${commentId}`, { body });
    return response.data;
  },

  deleteComment: async (todoId: string, commentId: string): Promise<void> => {
    await api.delete(`/todos/${todoId}/comments/${commentId}`);
  },

  getActivity: async (todoId: string, before?: string): Promise<{ activities: TodoActivity[]; nextCursor: string | null }> => {
    const response = await api.get(`/todos/${todoId}/activity`, { params: { limit: 20, ...(before ? { before } : {}) } });
    return response.data;
  },

  createSubtask: async (todoId: string, title: string): Promise<Subtask> => {
    const response = await api.post(`/todos/${todoId}/subtasks`, { title });
    return response.data;
  },

  updateSubtask: async (todoId: string, subtaskId: string, updates: { title?: string; completed?: boolean }): Promise<Subtask> => {
    const response = await api.patch(`/todos/${todoId}/subtasks/${subtaskId}`, updates);
    return response.data;
  },

  toggleSubtask: async (todoId: string, subtaskId: string): Promise<{ subtask: Subtask; stats: SubtaskStats }> => {
    const response = await api.post(`/todos/${todoId}/subtasks/${subtaskId}/toggle`);
    return response.data;
  },

  deleteSubtask: async (todoId: string, subtaskId: string): Promise<void> => {
    await api.delete(`/todos/${todoId}/subtasks/${subtaskId}`);
  },

  startTodo: async (todoId: string): Promise<Todo> => {
    const response = await api.post(`/todos/${todoId}/start`);
    return response.data;
  },

  stopTodo: async (todoId: string, note?: string): Promise<Todo> => {
    const response = await api.post(`/todos/${todoId}/stop`, note ? { note } : {});
    return response.data;
  },

  // Timely Feature Methods
  addToTimely: async (todoId: string): Promise<Todo> => {
    const response = await api.post(`/todos/${todoId}/timely`);
    return response.data;
  },

  removeFromTimely: async (todoId: string): Promise<Todo> => {
    const response = await api.delete(`/todos/${todoId}/timely`);
    return response.data;
  },

  createQuickTimelyTodo: async (todoData: { title: string; description?: string; priority?: 'low' | 'medium' | 'high' }): Promise<Todo & { subGoalTitle?: string; goalTitle?: string; goalId?: string }> => {
    const response = await api.post('/timely/todos', todoData);
    return response.data;
  },

  getTimelyTodos: async (): Promise<{ todos: (Todo & { subGoalTitle?: string; goalTitle?: string; goalId?: string; activeTimeEntry?: TimeEntry | null })[] }> => {
    const response = await api.get('/timely/todos');
    return response.data;
  },

  getCompletedTimelyTodos: async (): Promise<{ todos: (Todo & { subGoalTitle?: string; goalTitle?: string; goalId?: string })[] }> => {
    const response = await api.get('/timely/todos/completed');
    return response.data;
  },

  getTimeEntries: async (todoId: string): Promise<{ timeEntries: TimeEntry[]; totalTimeSpent: number }> => {
    const response = await api.get(`/todos/${todoId}/time-entries`);
    return response.data;
  },

  updateTimeEntry: async (todoId: string, entryId: string, data: { note?: string; durationSeconds?: number }): Promise<TimeEntry> => {
    const response = await api.put(`/todos/${todoId}/time-entries/${entryId}`, data);
    return response.data;
  },

  deleteTimeEntry: async (todoId: string, entryId: string): Promise<void> => {
    await api.delete(`/todos/${todoId}/time-entries/${entryId}`);
  },

  sendGoalSummary: async (goalId: string): Promise<void> => {
    await api.post(`/goals/${goalId}/send-summary`);
  },
};

// Time Entry interface
export interface TimeEntry {
  id: string;
  todoId: string;
  startedAt: string;
  stoppedAt: string | null;
  durationSeconds: number;
  note: string | null;
  createdAt: string;
}
